import {createAsyncThunk, createSlice, PayloadAction} from "@reduxjs/toolkit";
import {handlerError} from "../../../store/thunkHandlers";
import {emojiAPI} from "../api";
import {ICustomEmoji} from "./types";

export interface IEmojiState {
    /** Командные эмодзи компании, по имени ↑ — пикер, автодополнение, «Мои и командные эмодзи». */
    list: ICustomEmoji[];
    /**
     * Удалённые (мягко, S4.1): только чтобы старые сообщения и реакции
     * показывали картинку. В пикер и автодополнение не попадают.
     */
    deleted: ICustomEmoji[];
    loaded: boolean;
}

const initialState: IEmojiState = {list: [], deleted: [], loaded: false};

const byName = (a: ICustomEmoji, b: ICustomEmoji) => a.name.localeCompare(b.name);

/** Список и удалённые — одним thunk'ом: карта для сообщений нужна целиком. */
export const fetchEmojiList = createAsyncThunk<
    {list: ICustomEmoji[]; deleted: ICustomEmoji[]},
    void,
    {rejectValue: string}
>("emoji/list", async (_, {rejectWithValue}) => {
    try {
        const [list, deleted] = await Promise.all([emojiAPI.list(), emojiAPI.listDeleted()]);
        return {list, deleted};
    } catch (e) {
        return rejectWithValue(handlerError(e));
    }
});

/** Ошибка — текстом для окна (409 «Такое эмодзи уже есть», 413, 400 — из `message` ответа). */
export const fetchCreateEmoji = createAsyncThunk<ICustomEmoji, {name: string; file: File}, {rejectValue: string}>(
    "emoji/create",
    async ({name, file}, {rejectWithValue}) => {
        try {
            return await emojiAPI.create(name, file);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

/** Удалить своё (чужое бэкенд не даст — 403). Ошибка — текстом для окна. */
export const fetchDeleteEmoji = createAsyncThunk<{id: string}, string, {rejectValue: string}>(
    "emoji/delete",
    async (id, {rejectWithValue}) => {
        try {
            return await emojiAPI.remove(id);
        } catch (e) {
            return rejectWithValue(handlerError(e));
        }
    },
);

/** Убрать из списка: эмодзи уходит в удалённые, картинка в старых сообщениях остаётся. */
const retire = (state: IEmojiState, ids: Set<string>) => {
    const gone = state.list.filter((e) => ids.has(e.id));
    if (gone.length === 0) return;
    state.list = state.list.filter((e) => !ids.has(e.id));
    const known = new Set(state.deleted.map((e) => e.id));
    state.deleted = [...state.deleted, ...gone.filter((e) => !known.has(e.id))].sort(byName);
};

export const EmojiSlice = createSlice({
    name: "emoji",
    initialState,
    reducers: {
        /** `emoji:custom` — весь список компании после изменения; пропавшие из него — удалены. */
        emojiListReplaced(state, action: PayloadAction<ICustomEmoji[]>) {
            const next = new Set(action.payload.map((e) => e.id));
            retire(state, new Set(state.list.filter((e) => !next.has(e.id)).map((e) => e.id)));
            state.list = [...action.payload].sort(byName);
            state.loaded = true;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchEmojiList.fulfilled, (state, action) => {
            state.list = [...action.payload.list].sort(byName);
            state.deleted = [...action.payload.deleted].sort(byName);
            state.loaded = true;
        });
        builder.addCase(fetchCreateEmoji.fulfilled, (state, action) => {
            if (state.list.some((e) => e.id === action.payload.id)) return;
            state.list = [...state.list, action.payload].sort(byName);
        });
        builder.addCase(fetchDeleteEmoji.fulfilled, (state, action) => {
            retire(state, new Set([action.payload.id]));
        });
    },
});

export const {emojiListReplaced} = EmojiSlice.actions;

export default EmojiSlice.reducer;
